import { createAction } from '@reduxjs/toolkit';

export const setSortBy = createAction('projectBank/setSortBy');

export const closeProjectBank = createAction('projectBank/closeProjectBank');

export const setSelectedTilaajavastuuBusinessId = createAction(
  'projectBank/setSelectedTilaajavastuuBusinessId'
);

export const openProjectTilaajavastuuFolder = createAction(
  'project-bank/openProjectTilaajavastuuFolder'
);

export const openProjectTreeFolders = createAction(
  'project-bank/openProjectTreeFolders'
);

export const openPrivateProjectBankTreeFolders = createAction(
  'project-bank/openPrivateProjectBankTreeFolders'
);

export const closeProjectTreeFolders = createAction(
  'project-bank/closeProjectTreeFolders'
);

export const closePrivateProjectBankTreeFolders = createAction(
  'project-bank/closePrivateProjectBankTreeFolders'
);

export const openPdfPreviewDialog = createAction(
  'project-bank/openPdfPreviewDialog'
);

export const closePdfPreviewDialog = createAction(
  'project-bank/closePdfPreviewDialog'
);

export const setProjectBankViewMode = createAction(
  'project-bank/setProjectBankViewMode'
);

export const toggleSelectProjectBankFile = createAction(
  'project-bank/toggleSelectProjectBankFile'
);

export const clearSelectedProjectBankFiles = createAction(
  'project-bank/clearSelectedProjectBankFiles'
);

export const setIsProjectBankFileSelectDialogOpen = createAction(
  'project-bank/setIsProjectBankFileSelectDialogOpen'
);

// Closing the file selector dialog resets this whole slice (see the slice):
// right for a selector opened over some other scene, wrong for one opened
// over the project bank itself. A dialog that opens the selector over the
// bank takes a snapshot of the slice first and puts it back with this once
// the selector has closed (ProjectEmailDialog).
export const restoreProjectBankState = createAction(
  'project-bank/restoreProjectBankState'
);

export const openTransferFolderStructureDialog = createAction(
  'projectBank/openTransferFolderStructureDialog'
);

export const closeTransferFolderStructureDialog = createAction(
  'projectBank/closeTransferFolderStructureDialog'
);
export const setUpdateItemsInState = createAction(
  'projectBank/setUpdateItemsInState'
);

export const setSelectedPrivateProjectBankFolderId = createAction(
  'projectBank/setSelectedPrivateProjectBankFolderId'
);

export const setSelectedProjectBankVisibilityMode = createAction(
  'projectBank/setSelectedProjectBankVisibilityMode'
);

export const setSelectedProjectBankDataTransferTargetVisibilityMode =
  createAction(
    'projectBank/setSelectedProjectBankDataTransferTargetVisibilityMode'
  );

export const setIsAddFolderDialogOpen = createAction(
  'projectBank/setIsAddFolderDialogOpen'
);

export const selectPrivateProjectBankItem = createAction(
  'projectBank/selectPrivateProjectBankItem'
);

export const setSelectedPrivateProjectBankItems = createAction(
  'projectBank/setSelectedPrivateProjectBankItems'
);

export const clearSelectedPrivateProjectBankItems = createAction(
  'projectBank/clearSelectedPrivateProjectBankItems'
);

export const setBankSearchText = createAction('projectBank/setBankSearchText');

export const updateBankFilters = createAction('projectBank/updateBankFilters');

export const clearBankFilters = createAction('projectBank/clearBankFilters');

/**
 * Hide a bank row that was deleted somewhere other than this screen — by
 * Nerobotti, today — knowing only its id.
 *
 * Payload `{ id, deletedAt }`. It sets `deletedAt` on the row in whichever of
 * the two adapters already holds it, which is what the bank's own delete ends
 * in: every file and folder selector filters `!deletedAt`, so the row leaves
 * both the list and the tree.
 *
 * Not `setUpdateItemsInState`, which needs the row's `type` to pick an adapter
 * and upserts — given only an id, a deleted folder would be inserted into the
 * files adapter as a stray partial entity while the real one stayed on screen.
 * A row this client never loaded is left alone: there is nothing stale to fix.
 */
export const markProjectBankItemDeleted = createAction(
  'projectBank/markProjectBankItemDeleted'
);

/**
 * Take a file out of the folder it was in when it has been moved somewhere
 * other than this screen — by Nerobotti, today — knowing only its id.
 *
 * Payload is the id. It removes the row from the files adapter, which is what
 * the bank's own move ends in for every folder but the target
 * (`transferItems.fulfilled`). A refetch cannot do this part: a folder's
 * contents land with `setMany`, which never removes a row, so the old entity
 * would stay with its old `parent` and go on showing in the folder it left.
 * Refetching the open folder afterwards brings the file back if that is where
 * it went. A folder id, or a row this client never loaded, changes nothing.
 */
export const removeMovedProjectBankFile = createAction(
  'projectBank/removeMovedProjectBankFile'
);

/**
 * Show in the tree a folder that was made somewhere other than this screen —
 * by Nerobotti, today — knowing only its id. Payload is the id.
 *
 * The tree lists folders in creation order and, on a short pane, is squeezed
 * to about five rows that scroll on their own. A new folder therefore lands
 * below the fold, and a tree that did update reads as one that did not. The
 * slice opens the folders above the new one; the tree pane scrolls its row
 * into view and clears the request. An id that is not a loaded folder — a
 * file, or another project's row — asks for nothing.
 */
export const revealProjectBankTreeFolder = createAction(
  'projectBank/revealProjectBankTreeFolder'
);

/** The tree pane, done with a reveal whether or not its row was on screen. */
export const clearRevealedProjectBankTreeFolder = createAction(
  'projectBank/clearRevealedProjectBankTreeFolder'
);

/**
 * Put the bank at a location the URL names (utils/bankLocation) in one
 * state change: the visibility mode, the public folder or the tilaajavastuu
 * view or the private folder, and the tree rows to open. One change rather
 * than the handlers' several, so that what follows the location (the URL
 * sync) never sees a half-applied one.
 *
 * Payload `{ location, treeFolderIdsToOpen, privateTreeFolderIdsToOpen }`;
 * a public location carries the folder's `path` as well.
 */
export const applyBankLocation = createAction('projectBank/applyBankLocation');
